/**
 * Client-side helpers for the Capture "Export all" action (C15.13).
 *
 * The backend endpoint (`GET /capture/items/export/`) returns a JSON MANIFEST of
 * every capture item the caller owns — each note's text plus a direct
 * `media_url` + `filename` for photo/doc/video/audio — rather than a
 * server-built zip. A server-side zip of a large media library would buffer
 * every object's bytes through the shared web dyno (OOM risk); the manifest
 * hands the client direct URLs instead (see capture/api/viewsets.py `export`).
 *
 * These helpers turn that JSON manifest into a spreadsheet-friendly CSV the
 * user can open in Excel or Google Sheets. They are deliberately pure
 * (filename / normalize / serialize) so the export logic is unit-testable
 * without a browser; the one side-effecting helper injects its `document` +
 * `URL` dependencies for the same reason.
 */

import { captureTypeLabel } from "./Capture.helpers";
import { captureFileName } from "./captureDisplayName";

const pad2 = (n) => String(n).padStart(2, "0");

/** Column order of the downloaded spreadsheet. */
export const CSV_HEADERS = [
  "Title",
  "Type",
  "Notes",
  "Link",
  "File name",
  "Media URL",
  "Attached wheel",
  "Created",
];

/**
 * The download filename: `iyuga-capture-export-YYYY-MM-DD.csv` (local date).
 * Stable, sortable, and obviously an iYuga capture export at a glance.
 */
export const buildExportFilename = (date = new Date()) => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `iyuga-capture-export-${y}-${m}-${d}.csv`;
};

/**
 * Normalize the API response into the manifest object we serialize.
 *
 * Accepts either the unwrapped manifest (`{count, items, generated_at}`) or the
 * app's success-wrapper shape (`{data: {...}}`), so it does not care which layer
 * hands it the payload. ALWAYS returns an object whose `items` is an array
 * (never undefined) and whose `count` falls back to the array length — so an
 * empty or malformed response degrades to an empty, valid export rather than
 * throwing.
 */
export const normalizeExportManifest = (payload) => {
  const wrapped =
    payload && payload.data && typeof payload.data === "object"
      ? payload.data
      : payload;
  const src = wrapped && typeof wrapped === "object" ? wrapped : {};
  const items = Array.isArray(src.items) ? src.items : [];
  return {
    generated_at: src.generated_at || null,
    count: typeof src.count === "number" ? src.count : items.length,
    items,
  };
};

/**
 * RFC-4180 field escaping: wrap in double quotes and double any embedded quote
 * whenever the field contains a comma, quote, CR or LF. Neutralise spreadsheet
 * formula injection (`= + - @`) by prefixing a single quote (OWASP CSV-injection).
 */
export const escapeCsvField = (value) => {
  if (value === null || value === undefined) return "";
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const cell = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  return str;
};

const itemTitle = (item) => {
  const titled =
    (typeof item?.title === "string" && item.title.trim()) ||
    (typeof item?.name === "string" && item.name.trim()) ||
    "";
  if (titled) return titled;
  return itemFileName(item);
};

const itemFileName = (item) => {
  if (typeof item?.filename === "string" && item.filename.trim()) {
    return item.filename.trim();
  }
  return captureFileName(item) || "";
};

const itemCreated = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
};

/** Map one capture item -> the report columns (raw values, pre-escape). */
export const toCsvRow = (item = {}) => [
  itemTitle(item),
  captureTypeLabel(item.type),
  cell(item.text_content),
  cell(item.source_url),
  itemFileName(item),
  cell(item.media_url),
  cell(item.attached_wheel_name),
  itemCreated(item.created_at),
];

/**
 * Pretty-printed CSV string for the downloaded file.
 * Always emits the header even for an empty list so the file is a valid,
 * self-describing spreadsheet. Lines joined with CRLF per RFC-4180.
 */
export const serializeExportManifest = (manifest) => {
  const { items } = normalizeExportManifest(manifest);
  const lines = [CSV_HEADERS.map(escapeCsvField).join(",")];
  for (const item of items) {
    lines.push(toCsvRow(item).map(escapeCsvField).join(","));
  }
  return lines.join("\r\n");
};

/**
 * Trigger a browser download of the manifest as a CSV file.
 *
 * The `document` + `URL` dependencies are injected (defaulting to the globals)
 * so this is unit-testable without a real browser — a test passes fakes and
 * asserts the anchor's `download` name, that an object URL was created from the
 * blob, and that it was revoked afterwards (no leak).
 *
 * A UTF-8 BOM is prepended so Excel on Windows recognises the encoding.
 *
 * @returns {string} the filename used for the download.
 */
export const downloadExportManifest = (
  manifest,
  {
    doc = typeof document !== "undefined" ? document : undefined,
    url = typeof URL !== "undefined" ? URL : undefined,
    date,
  } = {}
) => {
  if (!doc || !url || typeof url.createObjectURL !== "function") {
    throw new Error("downloadExportManifest requires a document and URL");
  }
  const filename = buildExportFilename(date);
  const csv = serializeExportManifest(manifest);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const href = url.createObjectURL(blob);
  const anchor = doc.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  doc.body.appendChild(anchor);
  anchor.click();
  doc.body.removeChild(anchor);
  url.revokeObjectURL(href);
  return filename;
};
